import { Step, UpholsteryCar } from "../types"

interface StepConfiguration {
  [stepKey: string]: {
    selectedValue: string
    sku?: string
    image?: string
    label?: string
    substeps?: {
      [substepKey: string]: {
        value: string
        sku?: string
        image?: string
        label?: string
      }
    }
  }
}

export const getInitialStepValue = (step: any) => {
  return step?.category?.optionValues?.[0]?.optionValue || ""
}

export const getInitialSubStepValue = (substep: any) => {
  return substep?.category?.optionValues?.[0]?.optionValue || ""
}

export const getPerforationOptions = (
  subStepKey: string,
  currentSubSteps?: UpholsteryCar["steps"][number]["substeps"]
) => {
  if (!currentSubSteps) {
    return []
  }

  const targetSubstep = currentSubSteps.find(
    (substep) => substep.key === subStepKey
  )

  if (!targetSubstep?.substeps || targetSubstep.substeps.length === 0) {
    return []
  }

  const perforationSubstep = targetSubstep.substeps.find(
    (nested) => nested.key === "perforation"
  )

  return perforationSubstep?.category?.optionValues || []
}

export const getDefaultValue = (
  substep: any,
  stepKey: string,
  subStepKey: string,
  perforationOptions: any[]
) => {
  const shouldUseCustomDefault =
    stepKey === "design" &&
    (subStepKey === "perforation" ||
      subStepKey === "color-insert-perforation") &&
    substep.defaultValue

  if (shouldUseCustomDefault) {
    // For color-insert-perforation: use perforationOptions (nested substeps)
    // For standalone perforation: use substep.category.optionValues
    const optionsToSearch =
      perforationOptions.length > 0
        ? perforationOptions
        : substep.category?.optionValues || []

    const defaultVal = substep.defaultValue
    const normalizedDefault =
      typeof defaultVal === "string" ? defaultVal.toLowerCase() : defaultVal
    const matchingOption = optionsToSearch.find((opt) => {
      const optVal = opt.optionValue
      return typeof optVal === "string" && typeof normalizedDefault === "string"
        ? optVal.toLowerCase() === normalizedDefault
        : optVal === normalizedDefault
    })

    if (matchingOption) return matchingOption
  }

  return substep.category?.optionValues?.[0]
}

export const saveToLocalStorage = (key: string, value: any) => {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    console.error("Error saving to local storage", error)
  }
}

export const getFromLocalStorage = (key: string) => {
  try {
    const value = localStorage.getItem(key)
    return value ? JSON.parse(value) : null
  } catch (error) {
    console.error("Error getting from local storage", error)
    return null
  }
}

export const isSubstepVisible = (
  substepKey: string,
  stepKey: string,
  configurations: StepConfiguration,
  steps?: Step[]
): boolean => {
  const patternValue = configurations.pattern?.substeps?.["pattern"]?.value
  const toneValue = configurations.design?.substeps?.["tone"]?.value

  // Special case: If Pattern = Factory Match and Tone = Two-tone, only show "color-insert-perforation" combined page
  if (
    stepKey === "design" &&
    patternValue === "factory-match" &&
    toneValue === "two-tone"
  ) {
    const hasColorInsertPerforation = steps
      ?.find((s) => s.key === "design")
      ?.substeps.some((s) => s.key === "color-insert-perforation")

    if (hasColorInsertPerforation) {
      // Only show the combined page, hide individual pages
      if (substepKey === "color-insert-perforation") {
        return true // Show combined page
      }
      if (substepKey === "color-insert" || substepKey === "perforation") {
        return false // Hide individual pages
      }
    }
  }

  // Rule: Show only perforation location when pattern is 'factory-match' and tone is 'single'
  if (
    stepKey === "design" &&
    patternValue === "factory-match" &&
    toneValue === "single"
  ) {
    const hasPerforation = steps
      ?.find((s) => s.key === "design")
      ?.substeps.some((s) => s.key === "perforation")

    if (hasPerforation) {
      if (substepKey === "perforation") {
        return true
      }
      if (
        substepKey === "color-insert" ||
        substepKey === "color-insert-perforation"
      ) {
        return false
      }
    }
  }

  // Rule: Show only color insert when pattern is NOT 'factory-match' and tone is 'two-tone'
  if (
    stepKey === "design" &&
    patternValue !== "factory-match" &&
    toneValue === "two-tone"
  ) {
    const hasColorInsert = steps
      ?.find((s) => s.key === "design")
      ?.substeps.some((s) => s.key === "color-insert")

    if (hasColorInsert) {
      if (substepKey === "color-insert") {
        return true
      }
      if (
        substepKey === "perforation" ||
        substepKey === "color-insert-perforation"
      ) {
        return false
      }
    }
  }

  // Rule: Hide color-insert and perforation when pattern is not 'factory-match' and tone is 'single'
  if (
    stepKey === "design" &&
    patternValue !== "factory-match" &&
    toneValue === "single"
  ) {
    if (
      substepKey === "color-insert" ||
      substepKey === "perforation" ||
      substepKey === "color-insert-perforation"
    ) {
      return false
    }
  }

  // Rule 1: Show perforation only when pattern is 'factory Match'
  if (stepKey === "design" && substepKey === "perforation") {
    if (patternValue !== "factory-match") {
      return false
    }
  }

  // Rule 2: Show design-pattern-location only when pattern substep is NOT 'factory-match'
  if (stepKey === "pattern" && substepKey === "design-pattern-location") {
    if (patternValue === "factory-match") {
      return false
    }
  }

  // Rule 3: Show color-insert only when tone is 'two-tone'
  if (stepKey === "design" && substepKey === "color-insert") {
    if (toneValue !== "two-tone") {
      return false
    }
  }

  // Rule 4: When 'single' tone is selected, only show primary-color in Material step
  if (stepKey === "material") {
    if (
      toneValue === "single" &&
      substepKey !== "primary-color" &&
      substepKey !== "stitch"
    ) {
      return false
    }
  }

  return true
}

export const isStepVisible = (
  stepKey: string,
  configurations: StepConfiguration
): boolean => {
  const buildType = configurations.build?.selectedValue

  if (!buildType) return true

  // These steps are only hidden when build is "factory"
  const customizationSteps = ["kit", "pattern", "design", "material"]

  if (buildType === "factory") {
    // Factory build: hide customization steps, but show factory-match
    if (customizationSteps.includes(stepKey)) {
      return false // Hide customization steps
    }
    return true // Show all other steps, including factory-match
  } else {
    // Customized build: show customization steps, but hide factory-match
    if (stepKey === "factory-match") {
      return false // Hide factory-match
    }
    return true // Show all other steps, including customization steps
  }
}

export const stepHasImages = (step: Step): boolean => {
  // Check if the step itself has images in its category options
  if (step.category?.optionValues) {
    const hasMainImages = step.category.optionValues.some(
      (option) => option.optionImage?.responsiveImage?.src
    )
    if (hasMainImages) return true
  }

  // Check if any substeps have images
  if (step.substeps && step.substeps.length > 0) {
    return step.substeps.some((substep) => {
      // Check substep category options
      if (substep.category?.optionValues) {
        const hasSubstepImages = substep.category.optionValues.some(
          (option) => option.optionImage?.responsiveImage?.src
        )
        if (hasSubstepImages) return true
      }

      // Check factory matches
      if (substep.factoryMatches) {
        const hasFactoryImages = substep.factoryMatches.some(
          (match) => match.image
        )
        if (hasFactoryImages) return true
      }

      // Check color insert matches
      if (substep.colorInsertMatches) {
        const hasColorInsertImages = substep.colorInsertMatches.some(
          (match) => match.image
        )
        if (hasColorInsertImages) return true
      }

      return false
    })
  }

  // Check factory matches at step level
  if (step.factoryMatches) {
    return step.factoryMatches.some((match) => match.image)
  }

  return false
}

export const substepHasImages = (step: Step, substepKey: string): boolean => {
  const substep = step.substeps.find((sub) => sub.key === substepKey)
  if (!substep) return false

  // Special handling for color-insert-perforation combined substep
  if (substepKey === "color-insert-perforation") {
    // Check if any related substeps have images
    const colorInsertSubstep = step.substeps.find(
      (sub) => sub.key === "color-insert"
    )
    const perforationSubstep = step.substeps.find(
      (sub) => sub.key === "perforation"
    )

    // Check color-insert substep for images
    if (colorInsertSubstep) {
      if (
        colorInsertSubstep.category?.optionValues?.some(
          (option) => option.optionImage?.responsiveImage?.src
        )
      )
        return true

      if (colorInsertSubstep.factoryMatches?.some((match) => match.image))
        return true
      if (colorInsertSubstep.colorInsertMatches?.some((match) => match.image))
        return true
    }

    // Check perforation substep for images
    if (perforationSubstep) {
      if (
        perforationSubstep.category?.optionValues?.some(
          (option) => option.optionImage?.responsiveImage?.src
        )
      )
        return true

      if (perforationSubstep.factoryMatches?.some((match) => match.image))
        return true
      if (perforationSubstep.colorInsertMatches?.some((match) => match.image))
        return true
    }

    // Also check the combined substep itself
    if (
      substep.category?.optionValues?.some(
        (option) => option.optionImage?.responsiveImage?.src
      )
    )
      return true

    if (substep.factoryMatches?.some((match) => match.image)) return true
    if (substep.colorInsertMatches?.some((match) => match.image)) return true

    return false
  }

  // Check substep category options
  if (substep.category?.optionValues) {
    const hasSubstepImages = substep.category.optionValues.some(
      (option) => option.optionImage?.responsiveImage?.src
    )
    if (hasSubstepImages) return true
  }

  // Check factory matches
  if (substep.factoryMatches) {
    const hasFactoryImages = substep.factoryMatches.some((match) => match.image)
    if (hasFactoryImages) return true
  }

  // Check color insert matches
  if (substep.colorInsertMatches) {
    const hasColorInsertImages = substep.colorInsertMatches.some(
      (match) => match.image
    )
    if (hasColorInsertImages) return true
  }

  return false
}

export const getCurrentStepHasImages = (
  selectedVehicle: UpholsteryCar | null,
  currentStepIndex: number,
  currentSubOption: string | null
): boolean => {
  if (!selectedVehicle?.steps || currentStepIndex < 0) return false

  const currentStep = selectedVehicle.steps[currentStepIndex]
  if (!currentStep) return false

  // If we're on a specific substep, check that substep
  if (currentSubOption) {
    return substepHasImages(currentStep, currentSubOption)
  }

  // Otherwise, check the entire step
  return stepHasImages(currentStep)
}
